import { Dialog, Notify } from 'quasar';
import QDialogCreateTicket from './QDialogCreateTicket.vue';
import {
  ITicketFileContext,
  ITicketFiltersRequestParams,
  ITicketTransactionContext,
} from '@/components/TicketsAccountingPeriod/ticketsTypes.ts';
import { CreateTaskInputDto, UpdateTaskInput, CreateMessageInputDto, type Mutation, Query } from '@/generated/graphql.ts';
import { apolloService } from '@/apollo/apolloRequests.ts';
import { CREATE_TICKET, REMOVE_TICKET, CHANGE_TICKET_STATUS, CHANGE_TICKET, SEND_TICKET_MESSAGE, UPDATE_TICKET_MESSAGE, DELETE_TICKET_MESSAGE, READ_MESSAGES, VALIDATE_CLIENT_TEXT, VALIDATE_TICKET_DRAFT } from './mutationsTickets.ts';
import { ValidationKind, type ValidateClientTextResult, type ValidateTicketDraftResult } from './ticketsTypes.ts';
import { GET_TICKETS_PERIOD_FOLDER, ACCOUNTING_TICKET_TEMPLATES } from './queriesTickets.ts';
import { useTicketsPermissions, NO_TICKETS_ACCESS_TOOLTIP } from './useTicketsPermissions.ts';

export const useTicketsComposable = () => {
  const { canManageTickets, ticketAccessTooltip } = useTicketsPermissions();

  const checkTicketsAccess = (): boolean => {
    if (!canManageTickets.value) {
      Notify.create({
        message: ticketAccessTooltip.value || NO_TICKETS_ACCESS_TOOLTIP,
        color: 'warning',
        position: 'top',
      });
      return false;
    }
    return true;
  };

  const handleOpenDialogAddTicket = (
    companyId: string,
    folderId: string,
    periodFolderId: string,
    periodName?: string | undefined,
    filesContext?: ITicketFileContext[],
    transactionContext?: ITicketTransactionContext,
  ) => {
    if (!checkTicketsAccess()) return;
    return new Promise<void>((resolve) => {
      const dialog = Dialog.create({
        component: QDialogCreateTicket,
        componentProps: {
          companyId,
          folderId,
          periodFolderId,
          periodName,
          filesContext,
          transactionContext,
        },
      });
      dialog.onOk(() => {
        resolve();
      });
    });
  };

  const readMessages = async (chatId: string, messagesIds?: string[]) => {
    if (!checkTicketsAccess()) return;
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        READ_MESSAGES,
        {
          chatId,
          msgIds: messagesIds,
        },
      ) as Mutation;
      return data.readMessages;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const createTicket = async (taskInputData: CreateTaskInputDto) => {
    if (!checkTicketsAccess()) return;
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        CREATE_TICKET,
        {
          input: taskInputData,
        },
      ) as Mutation;
      return data.createTask;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const changeSubjectTicket = async (ticketId: string, newSubject: string) => {
    if (!checkTicketsAccess()) return;
    const taskInputData: UpdateTaskInput = {
      subject: newSubject,
      taskId: ticketId,
    };
    if (!newSubject || !ticketId) return;
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        CHANGE_TICKET,
        {
          input: taskInputData,
        },
      ) as Mutation;
      return data.updateTask;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const sendTicketMessage = async (ticketId: string, periodFolderId?: string, message?: string, fileIds?: string[] | null) => {
    if (!checkTicketsAccess()) return;
    const taskInputData: CreateMessageInputDto = {
      msgText: message,
      fileIds,
      periodIds: periodFolderId ? [periodFolderId] : null,
    };
    // Allow sending if we have either a message or files
    if (((!message || message.length === 0) && (!fileIds || fileIds.length === 0)) || !ticketId) return;
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        SEND_TICKET_MESSAGE,
        {
          chatId: ticketId,
          msg: taskInputData,
        },
        undefined,
        false,
      ) as Mutation;
      return data.sendMessage;
    } catch (err) {
      console.error(err);
      Notify.create({
        message: 'Failed to create question',
        color: 'negative',
        position: 'top',
      });
      throw err;
    }
  };

  const updateTicketMessage = async (ticketId: string, messageId: string, newMessage: string, fileIds?: string[]) => {
    if (!checkTicketsAccess()) return;
    const taskInputData: CreateMessageInputDto = {
      msgText: newMessage,
      fileIds,
    };
    if (((!newMessage || newMessage.length === 0) && (!fileIds || fileIds.length === 0)) || !ticketId || !messageId) return;
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        UPDATE_TICKET_MESSAGE,
        {
          chatId: ticketId,
          msgId: messageId,
          msg: taskInputData,
        },
      ) as Mutation;
      return data.updateMessage;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const deleteTicketMessage = async (ticketId: string, messageId: string) => {
    if (!checkTicketsAccess()) return;
    if (!ticketId || !messageId) return;
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        DELETE_TICKET_MESSAGE,
        {
          chatId: ticketId,
          msgId: messageId,
        },
      ) as Mutation;
      return data.deleteMessage;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const confirmAndRemoveTicket = async (taskId: string) => {
    if (!checkTicketsAccess()) return false;
    if (!taskId) return false;

    return new Promise((resolve, reject) => {
      Dialog.create({
        title: 'Confirm Deletion',
        message: 'Are you sure you want to delete the question and all related messages?',
        persistent: true,
        ok: {
          label: 'Delete',
          color: 'negative',
          flat: true,
          noCaps: true,
        },
        cancel: {
          label: 'Cancel',
          color: 'primary',
          flat: true,
          noCaps: true,
        },
      }).onOk(async () => {
        try {
          const result = await removeTicket(taskId);
          resolve(result);
        } catch (err) {
          console.error(err);
          reject(err);
        }
      }).onCancel(() => {
        resolve(false);
      });
    });
  };

  const removeTicket = async (taskId: string) => {
    if (!checkTicketsAccess()) return false;
    if (!taskId) return false;
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        REMOVE_TICKET,
        {
          taskId,
        },
      ) as Mutation;
      return data.deleteTask;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const changeTicketStatus = async (taskId: string, status: string) => {
    if (!checkTicketsAccess()) return;
    if (!taskId || !status) return false;
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        CHANGE_TICKET_STATUS,
        {
          taskId,
          status,
        },
      ) as Mutation;
      return data.changeTaskStatus;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const updateTicketLinkedFiles = async (ticketId: string, fileIds: string[]) => {
    if (!checkTicketsAccess()) return;
    if (!ticketId || !fileIds) return;
    const taskInputData: UpdateTaskInput = {
      taskId: ticketId,
      fileIds,
    };
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        CHANGE_TICKET,
        {
          input: taskInputData,
        },
      ) as Mutation;
      return data.updateTask;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const updateTicketLinkedTransactions = async (ticketId: string, transactionIds: string[]) => {
    if (!checkTicketsAccess()) return;
    if (!ticketId || !transactionIds) return;
    const taskInputData: UpdateTaskInput = {
      taskId: ticketId,
      transactionIds,
    };
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        CHANGE_TICKET,
        {
          input: taskInputData,
        },
      ) as Mutation;
      return data.updateTask;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const getTicketPeriodFolder = async (
    {
      filters,
    }: ITicketFiltersRequestParams,
  ) => {
    try {
      return (await apolloService.makeApolloRequest(
        'query',
        GET_TICKETS_PERIOD_FOLDER,
        {
          filter: {
            chatIds: filters?.chatIds || [],
            chatPeriodIds: filters?.chatPeriodIds || [],
            companyIds: filters?.companyIds || [],
          },
          pagination: {
            offset: 0,
            limit: 1,
          },
        },
      )) as Query;
    } catch (error) {
      console.error('Error fetching folders company:', error);
      throw error;
    }
  };

  /**
   * Validates an accountant's draft before it is sent to a client.
   * Advisory only: returns whether the draft should be improved, the detected
   * issues, an improved English version and a Russian back-translation.
   * Returns undefined on error so callers can fail open (send as-is).
   */
  const validateClientText = async (
    draft: string,
    kind: ValidationKind = ValidationKind.Message,
    context?: string,
  ): Promise<ValidateClientTextResult | undefined> => {
    if (!checkTicketsAccess()) return;
    const text = draft?.trim();
    if (!text) return;
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        VALIDATE_CLIENT_TEXT,
        { draft: text, kind, context: context?.trim() || undefined },
      ) as { accounting?: { validateClientText?: ValidateClientTextResult } };
      return data.accounting?.validateClientText;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  /**
   * Validates a whole ticket draft (subject + first message) in one backend call.
   * The backend checks the message first and uses its AI-cleaned version as context
   * for the subject. Returns undefined for non-accountants; throws on error.
   */
  const validateTicketDraft = async (
    subject: string,
    message: string,
  ): Promise<ValidateTicketDraftResult | undefined> => {
    if (!checkTicketsAccess()) return;
    try {
      const data = await apolloService.makeApolloRequest(
        'mutation',
        VALIDATE_TICKET_DRAFT,
        { subject: subject?.trim() || undefined, message: message?.trim() || undefined },
      ) as { accounting?: { validateTicketDraft?: ValidateTicketDraftResult } };
      return data.accounting?.validateTicketDraft;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const getAccountingTicketTemplates = async () => {
    if (!checkTicketsAccess()) return;
    try {
      const data = await apolloService.makeApolloRequest(
        'query',
        ACCOUNTING_TICKET_TEMPLATES,
        undefined,
        undefined,
        false,
        { fetchPolicy: 'cache-and-network' },
      ) as Query;
      return data.accounting.accountingTicketTemplatesForAccountants;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  return {
    handleOpenDialogAddTicket,
    createTicket,
    getTicketPeriodFolder,
    confirmAndRemoveTicket,
    changeTicketStatus,
    changeSubjectTicket,
    sendTicketMessage,
    updateTicketMessage,
    deleteTicketMessage,
    readMessages,
    validateClientText,
    validateTicketDraft,
    getAccountingTicketTemplates,
    updateTicketLinkedFiles,
    updateTicketLinkedTransactions,
  };
};
